import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchSupportAttachmentUrl } from '@/api/support/supportApi';
import type { SupportAttachment, SupportScope } from '@/api/support/supportApi.types';
import { FilePreviewModal } from '@/components/common/FilePreviewModal';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { cn } from '@/lib/utils';
import { buildMediaKindTranslationKey } from './supportLabels';

interface SupportMediaPreviewProps {
  scope: SupportScope;
  attachment: SupportAttachment;
  isOwnMessage: boolean;
}

export function SupportMediaPreview({ scope, attachment, isOwnMessage }: SupportMediaPreviewProps) {
  const { t } = useTranslation('support');
  const [isOpening, setIsOpening] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  async function openPreview() {
    setIsOpening(true);
    try {
      const freshDownloadUrl = await fetchSupportAttachmentUrl(scope, attachment.id);
      setPreviewUrl(freshDownloadUrl);
    } finally {
      setIsOpening(false);
    }
  }

  function resolveFreshDownloadUrl(): Promise<string> {
    return fetchSupportAttachmentUrl(scope, attachment.id, 'attachment');
  }

  function resolvePreviewImageUrl(): string {
    if (attachment.thumbnailUrl !== null) {
      return attachment.thumbnailUrl;
    }
    if (attachment.downloadUrl !== null) {
      return attachment.downloadUrl;
    }
    return '';
  }

  return (
    <>
      {attachment.mediaKind === 'image' ? (
        <button
          type="button"
          onClick={openPreview}
          disabled={isOpening}
          aria-label={t('support:media.open')}
          className="relative block w-full max-w-[12rem] aspect-[4/3] rounded-xl overflow-hidden border border-zooborder bg-zoooffwhite"
        >
          {attachment.thumbnailUrl === null && attachment.downloadUrl === null ? (
            <div className="w-full h-full flex items-center justify-center">
              <Icon name="Image" className="text-zooviolet text-2xl" />
            </div>
          ) : (
            <img
              src={resolvePreviewImageUrl()}
              alt={attachment.filename}
              className="w-full h-full object-cover"
            />
          )}
          {isOpening ? (
            <div className="absolute inset-0 flex items-center justify-center bg-zooindigo bg-opacity-40">
              <LoadingSpinner className="w-6 h-6" />
            </div>
          ) : null}
        </button>
      ) : attachment.mediaKind === 'video' ? (
        <button
          type="button"
          onClick={openPreview}
          disabled={isOpening}
          aria-label={t('support:media.play')}
          className="w-full max-w-[12rem] aspect-[4/3] rounded-xl overflow-hidden border border-zooborder bg-zooindigo bg-opacity-90 flex items-center justify-center text-zoowhite gap-2"
        >
          {isOpening ? <LoadingSpinner className="w-6 h-6" /> : <Icon name="Play" />}
        </button>
      ) : (
        <button
          type="button"
          onClick={openPreview}
          disabled={isOpening}
          className={cn(
            'flex items-center gap-2 px-3 py-2 rounded-xl border text-sm font-medium max-w-[220px]',
            isOwnMessage
              ? 'border-zoowhite border-opacity-40 bg-zooviolet bg-opacity-40 text-zoowhite'
              : 'border-zooborder bg-zoooffwhite text-zooindigo'
          )}
        >
          <Icon name="FileText" className="shrink-0" />
          <span className="truncate">{attachment.filename}</span>
          {isOpening ? <LoadingSpinner className="w-4 h-4 shrink-0" /> : null}
          <span className="sr-only">{t(buildMediaKindTranslationKey(attachment.mediaKind))}</span>
        </button>
      )}
      <FilePreviewModal
        isOpen={previewUrl !== null}
        onClose={function closePreview() {
          setPreviewUrl(null);
        }}
        url={previewUrl === null ? '' : previewUrl}
        filename={attachment.filename}
        contentType={attachment.contentType}
        resolveDownloadUrl={resolveFreshDownloadUrl}
      />
    </>
  );
}
